import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Building2, Users, FileText, ClipboardList, ShieldCheck, FileDown, Truck } from "lucide-react";
import { ViolationsWidget } from "@/components/ViolationsWidget";
import { EmailPacketDialog } from "@/components/EmailPacketDialog";
import { PacketShareHistory, PacketSchedulePanel } from "@/components/PacketSharePanel";
import { CompanyPanel } from "@/components/company/CompanyPanel";
import { HazmatWidget } from "@/components/company/HazmatWidget";
import { Contact, Flame } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export async function downloadPacketPdf(customerId) {
  const started = Date.now();
  const elapsed = () => Math.round((Date.now() - started) / 1000);
  const id = toast.loading("Building your packet…", { description: "Gathering drivers, fleet, certificates and photos", duration: Infinity });
  const tick = setInterval(() => toast.loading(`Building your packet… ${elapsed()}s`, { id, description: elapsed() > 8 ? "Large fleets take a little longer — still working" : "Gathering drivers, fleet, certificates and photos" }), 1000);
  try {
    const res = await api.get(`/customers/${customerId}/packet.pdf`, { responseType: "blob" });
    const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "compliance-packet.pdf";
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
    const pages = res.headers["x-page-count"];
    toast.success(pages ? `Packet ready · ${pages} pages` : "Insurance packet downloaded", { id, description: `${name} · ${(res.data.size / 1024 / 1024).toFixed(1)} MB in ${elapsed()}s`, duration: 6000 });
  } catch (err) { toast.error("Packet could not be generated", { id, description: formatApiError(err), duration: 8000 }); }
  finally { clearInterval(tick); }
}

export function PacketButton({ customerId, testid }) {
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { await downloadPacketPdf(customerId); } finally { setBusy(false); } };
  return <Button variant="outline" onClick={run} disabled={busy} data-testid={testid}><FileDown className={`h-4 w-4 mr-2 ${busy ? "animate-pulse" : ""}`} />{busy ? "Building packet…" : "Insurance packet (PDF)"}</Button>;
}

function expiryBadge(dateStr) {
  if (!dateStr) return <span className="text-slate-400">—</span>;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(dateStr) - today) / 86400000);
  if (days < 0) return <Badge className="bg-rose-50 text-rose-700 border border-rose-200">Expired · {dateStr}</Badge>;
  if (days <= 30) return <Badge className="bg-amber-50 text-amber-800 border border-amber-300">{days}d · {dateStr}</Badge>;
  return <span className="font-mono text-xs text-slate-600">{dateStr}</span>;
}

function consultantBadge(it) {
  if (!it.done) return <Badge className="bg-slate-100 text-slate-600 border border-slate-200">Not completed</Badge>;
  if (it.status === "overdue") return <Badge className="bg-rose-50 text-rose-700 border border-rose-200">Expired {it.next_due}</Badge>;
  if (it.status === "due-soon") return <Badge className="bg-amber-50 text-amber-800 border border-amber-300">{it.days_left}d · {it.next_due}</Badge>;
  if (it.status === "missing") return <Badge className="bg-amber-50 text-amber-800 border border-amber-300">Add dates</Badge>;
  return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">OK · {it.next_due}</Badge>;
}

const TONES = {
  blue: ["border-t-blue-500", "border-blue-100 bg-blue-50/60", "text-blue-700"],
  emerald: ["border-t-emerald-500", "border-emerald-100 bg-emerald-50/60", "text-emerald-700"],
  amber: ["border-t-amber-500", "border-amber-100 bg-amber-50/60", "text-amber-700"],
  violet: ["border-t-violet-500", "border-violet-100 bg-violet-50/60", "text-violet-700"],
  sky: ["border-t-sky-500", "border-sky-100 bg-sky-50/60", "text-sky-700"],
};

function Section({ icon: Icon, title, count, link, linkLabel, testid, tone = "blue", children }) {
  const [top, head, text] = TONES[tone] || TONES.blue;
  return (
    <Card className={`overflow-hidden border-t-4 ${top}`} data-testid={testid}>
      <div className={`px-5 py-3 border-b ${head} flex items-center justify-between`}>
        <div className="flex items-center gap-2 font-semibold text-slate-900"><Icon className={`h-4 w-4 ${text}`} />{title}<span className="text-xs font-mono text-slate-500">({count})</span></div>
        {link && <Link to={link} className={`text-xs ${text} hover:underline`}>{linkLabel} →</Link>}
      </div>
      {children}
    </Card>
  );
}

export default function CustomerProfile() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [shareKey, setShareKey] = useState(0);
  const [packetBusy, setPacketBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/customers/${id}/profile`).then(({ data: d }) => { if (!cancelled) setData(d); }).catch((err) => toast.error(formatApiError(err)));
    return () => { cancelled = true; };
  }, [id]);

  if (!data) return <div className="p-8 text-slate-500">Loading customer…</div>;
  const { customer: c, drivers, certificates, reports, consultant, consultant_items, vehicles = [], company_card: cc } = data;
  const downloadPacket = async () => { setPacketBusy(true); try { await downloadPacketPdf(c.id); } finally { setPacketBusy(false); } };
  const driverName = (did) => { const d = drivers.find((x) => x.id === did); return d ? `${d.first_name} ${d.last_name}` : "Company-level"; };
  const openReports = reports.filter((r) => ["pending", "in-progress"].includes(r.status)).length;
  const atRisk = drivers.filter((d) => [d.cdl_expiry, d.medical_card_expiry].some((x) => x && (new Date(x) - Date.now()) / 86400000 <= 30)).length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] space-y-6" data-testid="customer-profile-page">
      <div>
        <Link to="/customers" className="inline-flex items-center text-sm text-slate-500 hover:text-blue-700 mb-3" data-testid="customer-profile-back"><ArrowLeft className="h-4 w-4 mr-1" />All customers</Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            {cc?.logo_type && <img src={`${BACKEND}/api/company/${c.id}/logo`} alt="" className="h-16 w-16 rounded-lg border bg-white object-contain p-1" data-testid="customer-profile-logo" />}
            <div>
              <div className="tag-mono text-blue-700 mb-1 flex items-center gap-1"><Building2 className="h-3 w-3" />CUSTOMER PROFILE {cc?.is_hazmat && <Badge className="ml-2 bg-orange-100 text-orange-800 border border-orange-300"><Flame className="h-3 w-3 mr-1" />HAZMAT</Badge>}</div>
              <h1 className="text-2xl sm:text-3xl font-extrabold font-heading text-slate-900" data-testid="customer-profile-company">{c.company || c.name}</h1>
              <p className="text-slate-600 mt-1">{c.name} · <span className="font-mono text-sm">{c.email}</span> · joined {new Date(c.created_at).toLocaleDateString()}</p>
              {cc && <p className="text-sm text-slate-600 mt-1 font-mono" data-testid="customer-profile-ids">{cc.usdot ? `USDOT ${cc.usdot}` : "USDOT —"} · {cc.fein ? `FEIN ${cc.fein}` : "FEIN —"}{cc.mc_number ? ` · ${cc.mc_number}` : ""}{cc.der?.name ? <span className="font-sans"> · DER {cc.der.name}{cc.der.phone ? ` (${cc.der.phone})` : ""}</span> : ""}</p>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={downloadPacket} disabled={packetBusy} data-testid="customer-profile-packet-button"><FileDown className={`h-4 w-4 mr-2 ${packetBusy ? "animate-pulse" : ""}`} />{packetBusy ? "Building packet…" : "Insurance packet (PDF)"}</Button>
            <EmailPacketDialog customerId={c.id} company={c.company || c.name} onSent={() => setShareKey((k) => k + 1)} />
            <Link to={`/drivers?customer=${c.id}`}><Button variant="outline" data-testid="customer-profile-drivers-link">Drivers</Button></Link>
            <Link to={`/certificates?customer=${c.id}`}><Button variant="outline">Certificates</Button></Link>
            <Link to="/chat"><Button className="bg-blue-700 hover:bg-blue-800">Message</Button></Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Drivers", drivers.length, "kpi-drivers"], ["At-risk drivers", atRisk, "kpi-at-risk"], ["Certificates", certificates.length, "kpi-certs"], ["Open requests", openReports, "kpi-open"]].map(([l, v, t]) => (
          <Card key={l} className="p-5" data-testid={`customer-profile-${t}`}><div className="tag-mono text-slate-500 mb-2">{l}</div><div className={`text-3xl font-bold font-mono ${l === "At-risk drivers" && v > 0 ? "text-rose-700" : "text-slate-900"}`}>{v}</div></Card>
        ))}
      </div>

      <HazmatWidget customerId={c.id} isAdmin linkTo={() => `/customers/${c.id}#company`} />

      <Card className="p-5 border-t-4 border-t-slate-800" id="company" data-testid="customer-profile-company-panel">
        <div className="flex items-center gap-2 mb-4 font-semibold text-slate-900"><Contact className="h-4 w-4 text-slate-700" />Company card · secrets · hazmat</div>
        <CompanyPanel customerId={c.id} isAdmin defaultTab={sp.get("tab") || "card"} />
      </Card>

      {consultant && (
        <Section icon={ShieldCheck} title="Consultant · annual compliance" count={consultant_items.length} link="/consultant" linkLabel="Update" testid="customer-profile-consultant" tone="emerald">
          <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-200">
            {consultant_items.map((i) => { const it = consultant.items[i.key]; return (
              <div key={i.key} className="p-4 space-y-1">
                <div className="font-medium text-slate-900">{i.label}</div>
                <div className="font-mono text-xs text-slate-500">Completed {it.date || "—"}</div>
                {consultantBadge(it)}
              </div>
            ); })}
          </div>
          {consultant.notes && <div className="px-4 py-3 border-t border-slate-200 text-sm text-slate-700 whitespace-pre-wrap">{consultant.notes}</div>}
        </Section>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Section icon={Users} title="Drivers" count={drivers.length} link={`/drivers?customer=${c.id}`} linkLabel="Manage" testid="customer-profile-drivers">
          <Table>
            <TableHeader><TableRow><TableHead>Driver</TableHead><TableHead>License</TableHead><TableHead>Lic. expiry</TableHead><TableHead>Med card</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {drivers.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500">No drivers.</TableCell></TableRow>}
              {drivers.map((d) => (
                <TableRow key={d.id} data-testid={`profile-driver-${d.id}`}>
                  <TableCell className="font-medium">{d.first_name} {d.last_name}</TableCell>
                  <TableCell className="font-mono text-xs">{d.license_number} · {d.license_state}{d.is_cdl !== false && <Badge className="ml-1 bg-blue-50 text-blue-700 border border-blue-200 font-sans">CDL</Badge>}</TableCell>
                  <TableCell>{expiryBadge(d.cdl_expiry)}</TableCell>
                  <TableCell>{expiryBadge(d.medical_card_expiry)}</TableCell>
                  <TableCell><Badge variant="outline" className="capitalize">{d.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>

        <Section icon={FileText} title="Certificates" count={certificates.length} link={`/certificates?customer=${c.id}`} linkLabel="Manage" testid="customer-profile-certificates" tone="amber">
          <Table>
            <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Driver</TableHead><TableHead>Expiry</TableHead></TableRow></TableHeader>
            <TableBody>
              {certificates.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-slate-500">No certificates.</TableCell></TableRow>}
              {certificates.map((x) => (
                <TableRow key={x.id}><TableCell className="font-medium">{x.cert_type}</TableCell><TableCell className="text-sm">{x.driver_id ? driverName(x.driver_id) : <span className="text-slate-400">Company-level</span>}</TableCell><TableCell>{expiryBadge(x.expiry_date)}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <PacketSchedulePanel customerId={c.id} />
        <PacketShareHistory customerId={c.id} refreshKey={shareKey} />
      </div>

      <ViolationsWidget customerId={c.id} showCompany={false} />

      <Section icon={Truck} title="Fleet · trucks & trailers" count={vehicles.length} link={`/fleet?customer=${c.id}`} linkLabel="Manage" testid="customer-profile-fleet" tone="violet">
        <Table>
          <TableHeader><TableRow><TableHead>Unit</TableHead><TableHead>Vehicle</TableHead><TableHead>VIN</TableHead><TableHead>Plate</TableHead><TableHead>Registration</TableHead><TableHead>Annual insp.</TableHead><TableHead>Docs</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {vehicles.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-6 text-slate-500">No vehicles on file.</TableCell></TableRow>}
            {vehicles.map((v) => (
              <TableRow key={v.id} data-testid={`profile-vehicle-${v.id}`}>
                <TableCell className="font-semibold">{v.unit_number}<Badge variant="outline" className="ml-2 capitalize font-sans">{v.vehicle_type}</Badge></TableCell>
                <TableCell>{v.year} {v.make} {v.model}</TableCell>
                <TableCell className="font-mono text-xs">{v.vin}</TableCell>
                <TableCell className="font-mono text-xs">{v.plate || "—"} {v.plate_state}</TableCell>
                <TableCell>{expiryBadge(v.registration_expiry)}</TableCell>
                <TableCell>{expiryBadge(v.annual_inspection_expiry)}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono">{(v.documents || []).length}</Badge></TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{v.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section icon={ClipboardList} title="Report requests" count={reports.length} link="/reports" linkLabel="All requests" testid="customer-profile-reports" tone="sky">
        <Table>
          <TableHeader><TableRow><TableHead>Requested</TableHead><TableHead>Driver</TableHead><TableHead>Reports</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Billed</TableHead></TableRow></TableHeader>
          <TableBody>
            {reports.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500">No requests.</TableCell></TableRow>}
            {reports.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{r.driver_name || "—"}</TableCell>
                <TableCell><div className="flex flex-wrap gap-1">{r.report_types.map((t) => <Badge key={t} variant="outline" className="tag-mono">{t}</Badge>)}{r.rush && <Badge className="bg-amber-50 text-amber-800 border-amber-300">RUSH</Badge>}</div></TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{r.status}</Badge></TableCell>
                <TableCell className="text-right font-mono">{r.total == null ? "—" : `$${Number(r.total).toFixed(2)}`}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    </div>
  );
}
